import { useCallback, useEffect, useMemo, useState, memo } from 'react';
import PropTypes from 'prop-types';
import { useParams } from 'react-router-dom';
import { makeStyles } from '@material-ui/core';
import { useSelector } from 'react-redux';
import ActionButton from 'components/shared/actionButton';
import { ACTION_BUTTON_TYPES } from 'components/shared/actionButton/config';
import CustomButton from 'components/shared/customButton';
import CustomSwitch from 'components/shared/customSwitch';
import KeyJobInfo from 'components/shared/keyJobInfo';
import KeyJobSection from 'components/shared/keyJobSection';
import PageContainer from 'components/shared/pageContainer';
import ManageSuccessorForm from 'components/shared/manageSuccessorForm';
import StatusIndicator from 'components/shared/statusIndicator';
import UserAvatar from 'components/shared/userAvatar';
import { API_SUCCESSION_TALENT_POOLS } from 'constants/apiRoutes';
import { sticky } from 'constants/helperCssRules';
import { APP_PAGES } from 'constants/pages';
import {
  SUCCESSOR_FIELDS,
  SUCCESSOR_FORM_MODE,
  SUCCESSION_POOL_STATUS,
} from 'constants/succession';
import { successionKeyJobSelector } from 'store/selectors/successionKeyJobSelector';
import { successionSettingsSelector } from 'store/selectors/successionSettingsSelector';
import { peopleSelector } from 'store/selectors/peopleSelector';
import { getAllUsers } from 'store/modules/people';
import {
  clearSuccessionKeyJob,
  getSuccessionKeyJob,
  getKeyJobChangelog,
  getKeyJobSuccessors,
  patchSuccessionKeyJobSuccessor,
  postSuccessionKeyJobSuccessor,
  removeSuccessionKeyJobSuccessor,
  SUCCESSION_KEY_JOB_DEFAULT_PAGE,
} from 'store/modules/successionKeyJob';
import {
  getSuccessionReadiness,
  getSuccessionRemovalReasons,
  clearSuccessionSettings,
} from 'store/modules/successionSettings';
import { trimString } from 'utility/helpers';
import {
  prepareReadinessLevelOptions,
  prepareRemovalReasonOptions,
  prepareSuccessionTableActionColumn,
  isSuccessionUserRemoved,
} from 'utility/succession';
import http from 'utility/http';
import { hasNextPage, showSuccessMessage } from 'utility/uiUtils';
import { useTranslations } from 'utility/useTranslations';
import {
  prepareChangelogHeaders,
  prepareChangelogRows,
  prepareHolderHeaders,
  prepareSuccessorHeaders,
  prepareHolderRows,
  prepareSuccessorRows,
} from './config';

const { ADD, EDIT, REMOVE, READD } = SUCCESSOR_FORM_MODE;

const useStyles = makeStyles(({ palette: { primary }, spacing }) => ({
  divider: {
    paddingTop: spacing(8),
    ...sticky(primary.white, 105),
  },
  sectionActions: {
    display: 'flex',
    alignItems: 'center',
    gap: spacing(3),
  },
  showRemovedSwitch: {
    width: 'auto',
  },
  showRemovedLabel: {
    marginRight: spacing(1.5),
  },
  addSuccessorButton: {
    flexShrink: 0,
  },
  rowActions: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
  },
  changelogAction: {
    display: 'flex',
    alignItems: 'center',
    gap: spacing(1),
  },
}));

const SuccessionKeyJobPage = ({ location, navigate, dispatch, ...rest }) => {
  const classes = useStyles();
  const translations = useTranslations(APP_PAGES.SUCCESSION_KEY_JOB);

  const { jobId } = useParams();
  const { keyJob, changelog, holders, successors } = useSelector(
    successionKeyJobSelector
  );
  const settings = useSelector(successionSettingsSelector);
  const { allUsers } = useSelector(peopleSelector);

  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [shouldShowRemoved, setShouldShowRemoved] = useState(false);
  const [isManageDrawerOpened, setIsManageDrawerOpened] = useState(false);
  const [formData, setFormData] = useState(null);
  const [pools, setPools] = useState([]);
  const [successorsPage, setSuccessorsPage] = useState(
    SUCCESSION_KEY_JOB_DEFAULT_PAGE
  );
  const [changelogPage, setChangelogPage] = useState(
    SUCCESSION_KEY_JOB_DEFAULT_PAGE
  );

  const hasNextSuccessorsPage = useMemo(
    () => hasNextPage(successors),
    [successors]
  );

  const hasNextChangelogPage = useMemo(
    () => hasNextPage(changelog),
    [changelog]
  );

  const readinessOptions = useMemo(
    () => prepareReadinessLevelOptions(settings?.readiness_levels),
    [settings]
  );

  const removalReasons = useMemo(
    () => prepareRemovalReasonOptions(settings?.removal_reasons),
    [settings]
  );

  const onLoadMoreSuccessors = useCallback(() => {
    const nextPage = successorsPage + 1;
    setSuccessorsPage(nextPage);
    getKeyJobSuccessors(dispatch, jobId, shouldShowRemoved, true, nextPage);
  }, [dispatch, jobId, successorsPage, shouldShowRemoved]);

  const onLoadMoreChangelog = useCallback(() => {
    const nextPage = changelogPage + 1;
    setChangelogPage(nextPage);
    getKeyJobChangelog(dispatch, jobId, true, nextPage);
  }, [dispatch, jobId, changelogPage]);

  const handleOpenSuccessorForm = useCallback(
    (mode = ADD, successor = null) =>
      () => {
        setIsManageDrawerOpened(true);
        setFormData({ mode, successor });
      },
    []
  );

  const handleCloseSuccessorForm = useCallback(() => {
    setIsManageDrawerOpened(false);
  }, []);

  const handleSave = async payload => {
    const { USER, READINESS, RANK, NOTE, REASON, COMMENT } = SUCCESSOR_FIELDS;

    if (payload.mode === REMOVE) {
      await removeSuccessionKeyJobSuccessor(jobId, payload.successorId, {
        [REASON]: payload.reasonId,
        [COMMENT]: trimString(payload.comment || ''),
      });
      showSuccessMessage(translations.successMessages.removed);
    } else if (payload.mode === EDIT) {
      await patchSuccessionKeyJobSuccessor(jobId, payload.successorId, {
        [USER]: payload.successorId,
        [READINESS]: payload.readinessLevelId,
        [RANK]: Number(payload.rank),
        [NOTE]: trimString(payload.note || ''),
      });
      showSuccessMessage(translations.successMessages.updated);
    } else if (payload.mode === READD) {
      await postSuccessionKeyJobSuccessor(jobId, {
        [USER]: payload.successorId,
        [READINESS]: payload.readinessLevelId,
        [RANK]: Number(payload.rank),
        [NOTE]: trimString(payload.note || ''),
      });
      showSuccessMessage(translations.successMessages.readded);
    } else {
      await postSuccessionKeyJobSuccessor(jobId, {
        [USER]: payload.successorId,
        [READINESS]: payload.readinessLevelId,
        [RANK]: Number(payload.rank),
        [NOTE]: trimString(payload.note || ''),
      });
      showSuccessMessage(translations.successMessages.created);
    }

    setSuccessorsPage(SUCCESSION_KEY_JOB_DEFAULT_PAGE);
    setChangelogPage(SUCCESSION_KEY_JOB_DEFAULT_PAGE);
    await Promise.all([
      getSuccessionKeyJob(dispatch, jobId),
      getKeyJobSuccessors(dispatch, jobId, shouldShowRemoved),
      getKeyJobChangelog(dispatch, jobId),
    ]);
  };

  const handleShowRemovedChange = useCallback(
    isChecked => {
      setShouldShowRemoved(isChecked);
      setSuccessorsPage(SUCCESSION_KEY_JOB_DEFAULT_PAGE);
      getKeyJobSuccessors(
        dispatch,
        jobId,
        isChecked,
        false,
        SUCCESSION_KEY_JOB_DEFAULT_PAGE
      );
    },
    [dispatch, jobId]
  );

  const renderUser = useCallback(
    user => <UserAvatar user={user} variant="subtitle1" caption small />,
    []
  );

  const renderStatus = useCallback(
    status => <StatusIndicator status={status} />,
    []
  );

  const renderChangeLogEntryAction = useCallback(
    (user, text) => (
      <div className={classes.changelogAction}>
        {user ? (
          <UserAvatar user={user} variant="subtitle1" caption small />
        ) : null}
        <span>{text}</span>
      </div>
    ),
    [classes.changelogAction]
  );

  const holderHeaders = useMemo(
    () => prepareHolderHeaders(translations.columns),
    [translations.columns]
  );

  const holderRows = useMemo(
    () => prepareHolderRows(holders, translations, renderUser),
    [holders, translations, renderUser]
  );

  const changelogHeaders = useMemo(
    () => prepareChangelogHeaders(translations.columns),
    [translations.columns]
  );

  const changelogRows = useMemo(
    () =>
      prepareChangelogRows(
        changelog.results,
        translations,
        renderUser,
        renderChangeLogEntryAction
      ),
    [changelog.results, translations, renderUser, renderChangeLogEntryAction]
  );

  const successorActionsColumn = useMemo(
    () => prepareSuccessionTableActionColumn(translations.columns.actions),
    [translations.columns.actions]
  );

  const renderSuccessorActions = useCallback(
    successor => {
      if (isSuccessionUserRemoved(successor?.status)) {
        return (
          <div className={classes.rowActions}>
            <ActionButton
              type={ACTION_BUTTON_TYPES.PREVIEW}
              tooltipText={translations.menu.view}
              onClickHandler={handleOpenSuccessorForm(REMOVE, successor)}
            />
            <ActionButton
              type={ACTION_BUTTON_TYPES.RELOAD}
              tooltipText={translations.menu.readd}
              onClickHandler={handleOpenSuccessorForm(READD, successor)}
            />
          </div>
        );
      }

      return (
        <div className={classes.rowActions}>
          <ActionButton
            type={ACTION_BUTTON_TYPES.EDIT}
            tooltipText={translations.menu.edit}
            onClickHandler={handleOpenSuccessorForm(EDIT, successor)}
          />
          <ActionButton
            type={ACTION_BUTTON_TYPES.DELETE}
            tooltipText={translations.menu.remove}
            onClickHandler={handleOpenSuccessorForm(REMOVE, successor)}
          />
        </div>
      );
    },
    [classes.rowActions, translations.menu, handleOpenSuccessorForm]
  );

  const successorHeaders = useMemo(
    () => prepareSuccessorHeaders(translations.columns),
    [translations.columns]
  );

  const successorRows = useMemo(
    () =>
      prepareSuccessorRows(
        translations,
        successors.results,
        renderUser,
        renderSuccessorActions,
        renderStatus
      ),
    [
      successors.results,
      translations,
      renderUser,
      renderSuccessorActions,
      renderStatus,
    ]
  );

  const successorActions = (
    <div className={classes.sectionActions}>
      <CustomSwitch
        className={classes.showRemovedSwitch}
        labelClass={classes.showRemovedLabel}
        label={translations.showRemoved}
        isChecked={shouldShowRemoved}
        onChange={handleShowRemovedChange}
      />
      <CustomButton
        className={classes.addSuccessorButton}
        type="addDarkRoundedOutlined"
        onClick={handleOpenSuccessorForm()}
      >
        {translations.addSuccessor}
      </CustomButton>
    </div>
  );

  useEffect(() => {
    let active = true;

    const load = async () => {
      setIsInitialLoad(true);
      try {
        await Promise.all([
          getSuccessionReadiness(dispatch),
          getSuccessionRemovalReasons(dispatch),
          getSuccessionKeyJob(dispatch, jobId),
          getKeyJobSuccessors(dispatch, jobId),
          getKeyJobChangelog(dispatch, jobId),
          getAllUsers(dispatch),
          http
            .get(API_SUCCESSION_TALENT_POOLS, {
              params: { page_size: 500, status: SUCCESSION_POOL_STATUS.ACTIVE },
            })
            .then(({ data }) => {
              if (!active) return;
              setPools(data?.results || []);
            }),
        ]);
      } finally {
        if (active) setIsInitialLoad(false);
      }
    };

    load();
    return () => {
      active = false;
      clearSuccessionKeyJob(dispatch);
      clearSuccessionSettings(dispatch);
    };
  }, [dispatch, jobId]);

  return (
    <PageContainer
      {...rest}
      location={location}
      navigate={navigate}
      dispatch={dispatch}
      translations={translations}
      headerProps={{
        title: location?.state?.title || translations.title,
      }}
      shouldPassProps={false}
      isFullWidthContent
    >
      {!isInitialLoad && keyJob && (
        <>
          <div className={classes.divider} />
          <KeyJobInfo translations={translations} keyJob={keyJob} />
          <KeyJobSection
            title={translations.sections.holders}
            headers={holderHeaders}
            rows={holderRows}
            noResulltsMessage={translations.noHolders}
            tableTranslations={translations}
          />
          <KeyJobSection
            title={translations.sections.successors}
            actions={successorActions}
            headers={successorHeaders}
            headerActions={successorActionsColumn}
            rows={successorRows}
            noResulltsMessage={translations.noSuccessors}
            tableTranslations={translations}
            hasLoadMore={hasNextSuccessorsPage}
            onLoadMore={onLoadMoreSuccessors}
          />
          <KeyJobSection
            title={translations.sections.changelog}
            headers={changelogHeaders}
            rows={changelogRows}
            noResulltsMessage={translations.noChangelog}
            tableTranslations={translations}
            hasLoadMore={hasNextChangelogPage}
            onLoadMore={onLoadMoreChangelog}
          />
          <ManageSuccessorForm
            translations={translations.successorForm}
            isOpened={isManageDrawerOpened}
            mode={formData?.mode || ADD}
            successor={formData?.successor || null}
            employees={allUsers}
            pools={pools}
            readinessOptions={readinessOptions}
            removalReasons={removalReasons}
            onClose={handleCloseSuccessorForm}
            onSave={handleSave}
          />
        </>
      )}
    </PageContainer>
  );
};

SuccessionKeyJobPage.propTypes = {
  location: PropTypes.object.isRequired,
  navigate: PropTypes.func.isRequired,
  dispatch: PropTypes.func.isRequired,
};

export default memo(SuccessionKeyJobPage);
